import { useState, useEffect, useCallback } from 'react';

export interface AudioDevice {
  deviceId: string;
  label: string;
}

export function useAudioDevices() {
  const [devices, setDevices] = useState<AudioDevice[]>([]);
  const [selectedDeviceId, setSelectedDeviceId] = useState<string>('');
  const [hasPermission, setHasPermission] = useState(false);

  const getDevices = useCallback(async () => {
    try {
      // We need to ask for permission first to get device labels
      if (!hasPermission) {
        await navigator.mediaDevices.getUserMedia({ audio: true });
        setHasPermission(true);
      }

      const allDevices = await navigator.mediaDevices.enumerateDevices();
      const audioInputs = allDevices
        .filter(device => device.kind === 'audioinput')
        .map(device => ({
          deviceId: device.deviceId,
          label: device.label || `Microphone ${device.deviceId.slice(0, 5)}...`
        }));

      setDevices(audioInputs);

      // If no device is selected, select the default one or the first one
      if (!selectedDeviceId && audioInputs.length > 0) {
        const defaultDevice = audioInputs.find(d => d.deviceId === 'default');
        setSelectedDeviceId(defaultDevice ? defaultDevice.deviceId : audioInputs[0].deviceId);
      }
    } catch (error) {
      console.error('Error listing audio devices:', error);
    }
  }, [hasPermission, selectedDeviceId]);

  // Listen for device changes
  useEffect(() => {
    navigator.mediaDevices.addEventListener('devicechange', getDevices);
    return () => {
      navigator.mediaDevices.removeEventListener('devicechange', getDevices);
    };
  }, [getDevices]);

  return {
    devices,
    selectedDeviceId,
    setSelectedDeviceId,
    getDevices,
    hasPermission
  };
}

